import {
  DirectorySource,
  type EntraIntegrationStatus,
  type LdapIntegrationStatus,
} from '@gb-ticket/shared-types';
import { Loader2Icon, RefreshCwIcon } from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import {
  useDirectoryStatus,
  useStartDirectorySync,
} from '@/platform/features/directory/use-directory';

type SourceStatus = EntraIntegrationStatus | LdapIntegrationStatus;

export function DirectoryAdminPage() {
  usePageTitle('Verzeichnis');
  const status = useDirectoryStatus();
  const sync = useStartDirectorySync();

  async function start(source: DirectorySource, label: string) {
    try {
      await sync.mutateAsync(source);
      toast.success(`${label}: Abgleich eingereiht. Der Stand aktualisiert sich nach dem Lauf.`);
      window.setTimeout(() => void status.refetch(), 5000);
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError ? error.message : 'Der Abgleich konnte nicht starten.',
      );
    }
  }

  return (
    <section className="space-y-6">
      <PlatformPageHeader
        title="Verzeichnis"
        description="Abgleich der Benutzer mit Microsoft 365 und LDAP. Die Verbindung richtet die IT auf dem Server ein."
        action={
          <Button
            type="button"
            variant="outline"
            onClick={() => void status.refetch()}
            disabled={status.isFetching}
          >
            <RefreshCwIcon className="size-4" aria-hidden />
            Stand neu laden
          </Button>
        }
      />

      {status.isPending && (
        <p className="flex items-center gap-2 text-muted-foreground">
          <Loader2Icon className="size-4 animate-spin" aria-hidden />
          Stand wird geladen …
        </p>
      )}

      {status.isError && (
        <p role="alert" className="text-sm text-destructive">
          {status.error instanceof ApiRequestError
            ? status.error.message
            : 'Der Stand konnte nicht geladen werden.'}
        </p>
      )}

      {status.data && (
        <div className="grid gap-4 md:grid-cols-2">
          <SourceCard
            title="Microsoft 365 (Entra ID)"
            status={status.data.entra}
            busy={sync.isPending && sync.variables === DirectorySource.ENTRA}
            disabled={sync.isPending}
            onSync={() => void start(DirectorySource.ENTRA, 'Microsoft 365')}
          />
          <SourceCard
            title="LDAP / Active Directory"
            status={status.data.ldap}
            busy={sync.isPending && sync.variables === DirectorySource.LDAP}
            disabled={sync.isPending}
            onSync={() => void start(DirectorySource.LDAP, 'LDAP')}
          />
        </div>
      )}
    </section>
  );
}

function SourceCard({
  title,
  status,
  busy,
  disabled,
  onSync,
}: {
  title: string;
  status: SourceStatus;
  busy: boolean;
  disabled: boolean;
  onSync: () => void;
}) {
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle>{title}</CardTitle>
          {status.envConfigured ? (
            <Badge variant="outline">Eingerichtet</Badge>
          ) : (
            <Badge variant="secondary">Nicht eingerichtet</Badge>
          )}
        </div>
        <CardDescription>
          {status.lastSyncAt
            ? `Letzter Abgleich: ${formatDateTime(status.lastSyncAt)}`
            : 'Noch kein Abgleich gelaufen.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <dl className="grid grid-cols-3 gap-2 text-center">
          <Stat label="Neu" value={status.lastSyncCreated} />
          <Stat label="Geändert" value={status.lastSyncUpdated} />
          <Stat label="Deaktiviert" value={status.lastSyncDeactivated} />
        </dl>
        {status.lastError ? (
          <p role="alert" className="rounded-md bg-destructive/10 p-3 text-destructive">
            Letzter Fehler: {status.lastError}
          </p>
        ) : null}
        <Button type="button" onClick={onSync} disabled={disabled || !status.envConfigured}>
          {busy ? <Loader2Icon className="size-4 animate-spin" aria-hidden /> : null}
          Jetzt abgleichen
        </Button>
      </CardContent>
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-xl border border-border bg-muted/30 p-2">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-lg font-semibold tabular-nums">{value}</dd>
    </div>
  );
}
